import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type PropsWithChildren,
  type ReactNode
} from "react";
import { AlertCircle, BellRing, CheckCircle2, X } from "lucide-react";

export function Avatar({
  name,
  src,
  size = "md"
}: {
  name: string;
  src?: string | null;
  size?: "sm" | "md" | "lg" | "xl";
}) {
  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
  return (
    <span className={`avatar avatar-${size}`} aria-label={name}>
      {src ? <img src={src} alt="" /> : initials}
      <i className="online-dot" />
    </span>
  );
}

export function Badge({
  children,
  tone = "neutral",
  dot
}: PropsWithChildren<{
  tone?: "neutral" | "green" | "amber" | "red" | "blue";
  dot?: boolean;
}>) {
  return (
    <span className={`badge badge-${tone}`}>
      {dot ? <i /> : null}
      {children}
    </span>
  );
}

export function Button({
  variant = "primary",
  size = "md",
  className = "",
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "ghost" | "danger";
  size?: "sm" | "md" | "icon";
}) {
  return (
    <button
      className={`button button-${variant} button-${size} ${className}`}
      {...props}
    />
  );
}

export function Modal({
  open,
  onClose,
  title,
  eyebrow,
  children,
  width = "520px"
}: PropsWithChildren<{
  open: boolean;
  onClose: () => void;
  title: string;
  eyebrow?: string;
  width?: string;
}>) {
  if (!open) return null;
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={{ maxWidth: width }}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="modal-header">
          <div>
            {eyebrow ? <span className="eyebrow">{eyebrow}</span> : null}
            <h2>{title}</h2>
          </div>
          <button className="icon-button" onClick={onClose} aria-label="Fechar">
            <X size={19} />
          </button>
        </header>
        <div className="modal-body">{children}</div>
      </section>
    </div>
  );
}

interface ToastItem {
  id: number;
  message: string;
  tone: "success" | "warning" | "error";
}

const ToastContext = createContext<{
  success: (message: string) => void;
  warning: (message: string) => void;
  error: (message: string) => void;
} | null>(null);

export function ToastProvider({ children }: PropsWithChildren) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const recent = useRef(new Map<string, number>());
  const show = useCallback(
    (message: string, tone: ToastItem["tone"]) => {
      const key = `${tone}:${message}`;
      const now = Date.now();
      if (now - (recent.current.get(key) || 0) < 1500) return;
      recent.current.set(key, now);
      const id = Date.now() + Math.random();
      setToasts((current) => [...current, { id, message, tone }]);
      window.setTimeout(() => {
        setToasts((current) => current.filter((toast) => toast.id !== id));
      }, 3800);
    },
    []
  );
  const value = useMemo(
    () => ({
      success: (message: string) => show(message, "success"),
      warning: (message: string) => show(message, "warning"),
      error: (message: string) => show(message, "error")
    }),
    [show]
  );
  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-stack" aria-live="polite">
        {toasts.map((toast) => (
          <div className={`toast toast-${toast.tone}`} key={toast.id}>
            {toast.tone === "success" ? (
              <CheckCircle2 size={18} />
            ) : toast.tone === "warning" ? (
              <BellRing size={18} />
            ) : (
              <AlertCircle size={18} />
            )}
            <span>{toast.message}</span>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const value = useContext(ToastContext);
  if (!value) throw new Error("ToastProvider ausente.");
  return value;
}

export function FieldError({ field, message }: { field: string; message?: string }) {
  return message ? (
    <small id={`field-error-${field.replace(/[^a-z0-9_-]+/gi, "-")}`} className="field-error" role="alert">
      {message}
    </small>
  ) : null;
}

export function EmptyState({
  icon,
  title,
  description,
  action
}: {
  icon: ReactNode;
  title: string;
  description: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty-state">
      <span className="empty-icon">{icon}</span>
      <h3>{title}</h3>
      <p>{description}</p>
      {action}
    </div>
  );
}

export function SearchFallbackNotice() {
  return (
    <p className="search-fallback-notice" role="status">
      <strong>Nenhum resultado exato encontrado.</strong>
      <span>Exibindo correspondências semelhantes.</span>
    </p>
  );
}

export function Skeleton({ className = "" }: { className?: string }) {
  return <span className={`skeleton ${className}`} />;
}
